/**
 * packages/shared/src/scoring/recompute-telemetry-score.ts
 *
 * Single scoring service function (RUF-514) shared by:
 *   - apps/api's POST /:assetId/telemetry-score route (RUF-513, ops-triggered,
 *     trigger_id='manual_override')
 *   - apps/telemetry-ingest's nightly scheduled batch recompute (RUF-514,
 *     trigger_id='scheduled')
 *
 * Extracted from the RUF-513 route handler's inline logic so the two callers
 * can never drift: load the asset's telemetry_daily_agg rows for a trailing
 * window (RUF-517; defaults to DEFAULT_TRAILING_WINDOW_DAYS, configurable via
 * `windowDays`), map each day → engine inputs, roll the window up into one
 * input set (telemetry-window-rollup.ts), score against the v5 model, merge
 * in coverage, persist one telemetry_score_snapshot row stamped with the
 * window range.
 *
 * RUF-517 background: v1 scored only the single latest telemetry_daily_agg
 * row, which can be jumpy - a quiet/partial day skews the score. Rolling up
 * a trailing window smooths that out: `_Max` names take the max across the
 * window, `_Count`/`total_*` names sum, everything else (gauges, `_Current`)
 * takes the latest value seen in the window. See telemetry-window-rollup.ts.
 *
 * SIL-150 additionally, right after the window rollup:
 *   - resolves per-field source precedence (datadog > csv > model-derived)
 *     from telemetry_raw's row-level collection_method_id over a short
 *     freshness window, OVERRIDING the corresponding window-rolled values
 *     (telemetry-source-precedence.ts) - this is what stops a stale CSV
 *     upload from clobbering a live Datadog reading rolled into the same
 *     telemetry_daily_agg day;
 *   - fills a small set of trend-derived fields (ECC growth rates, GPU
 *     power/clock variance %) computed from the asset's own ingested
 *     telemetry_daily_agg series when no live/CSV value already covers them
 *     (telemetry-trend.ts, provisional pending SIL-145);
 *   - boosts confidenceTier when enough scored field weight came from a
 *     live Datadog source within the freshness window
 *     (confidence-tier.ts's applyMeasuredBoost).
 *
 * DB write note: no SET LOCAL ROLE here - mirrors the FMV recompute /
 * market-comp batch jobs (packages/shared/src/fmv-trigger.ts,
 * apps/ingestion/src/pipeline/mcmp.ts), which also insert directly through
 * whatever DB connection the caller passes in without a per-call role
 * switch. apps/api's route wraps this in ITS OWN per-request `SET LOCAL ROLE
 * ops_readonly` transaction (a defense-in-depth scoping of the caller's
 * privilege for THAT one HTTP request - see middleware/role.ts); a batch job
 * has no per-request actor to scope to and connects as its own service DB
 * role, exactly like every other apps/telemetry-ingest / apps/ingestion write.
 */

import type { Kysely, Selectable } from 'kysely';
import type { DB as Database } from '../db.js';
import { scoreAsset } from './engine.js';
import { telemetryScoringModel } from './models/telemetry-scoring.v5.js';
import { mapDailyAggToTelemetryInputs, type TelemetryInputLike } from './telemetry-daily-agg-mapping.js';
import {
  buildCoverageDenominators,
  computeCoverageFromInputs,
  coverageTier,
  mergeCategoryScoresWithCoverage,
} from './telemetry-coverage.js';
import {
  DEFAULT_TRAILING_WINDOW_DAYS,
  computeTrailingWindow,
  rollupTelemetryInputsOverWindow,
  type DailyTelemetryInputs,
} from './telemetry-window-rollup.js';
import { aggregateLifetimeDisclosureInputs, computeTelemetryDisclosures } from './telemetry-disclosures.js';
import { horizonBandFromScore } from './horizon-band.js';
import { wearTrajectory } from './wear-trajectory.js';
import { confidenceTier, durationTierFromDates } from './confidence-tier.js';
import {
  applyCappedConfidenceBumps,
  resolveVerificationConfidenceEffect,
  VERIFICATION_BUMP_TTL_DAYS,
  type VerificationRunForConfidence,
} from './verification-confidence.js';
import {
  FIELD_SOURCE_FRESHNESS_WINDOW_DAYS,
  mapRawRowToFieldObservations,
  mergePrecedenceOverBase,
  resolveFieldSourcePrecedence,
} from './telemetry-source-precedence.js';
import { computeGrowthRate, computeVariancePercent } from './telemetry-trend.js';

export type TelemetryScoreSnapshotRow = Selectable<Database['telemetryScoreSnapshot']>;

export type RecomputeTelemetryScoreResult =
  | { scored: false; reason: 'no telemetry' }
  | { scored: true; snapshot: TelemetryScoreSnapshotRow };

const MODEL_TELEMETRY_NAMES = new Set(telemetryScoringModel.rules.map((r) => r.telemetryName));

/** Convert a DB date-ish value (Date | string) to a `Date` for window math. */
function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

/** Pull one numeric key out of a telemetry_daily_agg.extra_aggregates jsonb value, or null. */
function extraAggregateNumber(extra: unknown, key: string): number | null {
  if (extra == null || typeof extra !== 'object' || Array.isArray(extra)) return null;
  const raw = (extra as Record<string, unknown>)[key];
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Score one asset's trailing window of telemetry_daily_agg rows (RUF-517;
 * `windowDays` days ending at the asset's latest reported day, default
 * DEFAULT_TRAILING_WINDOW_DAYS) and persist a telemetry_score_snapshot row.
 * Returns `{ scored: false }` (never throws) when the asset has no
 * telemetry_daily_agg row at all - a clean, unscored result, not an error.
 *
 * RUF-552 (passport engine, design D2/D4/D5) additionally derives, from the
 * SAME run:
 *   - disclosures[] - from the asset's FULL lifetime telemetry_daily_agg history
 *     (not the scoring window; see telemetry-disclosures.ts's module header for
 *     why disclosures must be window-independent).
 *   - horizonBand / wearTrajectory - from the windowed score/inputs already
 *     computed here (horizon-band.ts / wear-trajectory.ts).
 *   - confidenceTier - min(coverage tier, duration tier over the same
 *     lifetime span used for disclosures) (confidence-tier.ts).
 *
 * Caller is responsible for confirming the asset exists (both current
 * callers already do their own existence check for their own error shape).
 */
export interface RecomputeTelemetryScoreOptions {
  /**
   * Score "as of" this day: the trailing window ends at the latest
   * telemetry_daily_agg day <= asOfDay instead of the asset's overall latest.
   * Used by the history backfill to reconstruct past scores.
   */
  asOfDay?: Date;
  /**
   * Explicit scored_at for the snapshot row (backfill backdating). Defaults
   * to the column's now() default when omitted.
   */
  scoredAt?: Date;
}

export async function recomputeTelemetryScoreForAsset(
  db: Kysely<Database>,
  assetId: string,
  triggerId: string,
  windowDays: number = DEFAULT_TRAILING_WINDOW_DAYS,
  opts: RecomputeTelemetryScoreOptions = {},
): Promise<RecomputeTelemetryScoreResult> {
  // 1. Latest telemetry_daily_agg day for this asset - anchors the window end.
  let latestAggQuery = db
    .selectFrom('telemetryDailyAgg')
    .select('day')
    .where('assetId', '=', assetId)
    .orderBy('day', 'desc')
    .limit(1);
  if (opts.asOfDay) {
    latestAggQuery = latestAggQuery.where('day', '<=', opts.asOfDay);
  }
  const latestAgg = await latestAggQuery.executeTakeFirst();

  if (!latestAgg) {
    return { scored: false, reason: 'no telemetry' };
  }

  const windowEnd = toDate(latestAgg.day);
  const { start: windowStart } = computeTrailingWindow(windowEnd, windowDays);

  // 2. Every telemetry_daily_agg row within [windowStart, windowEnd] for this asset.
  const windowRows = await db
    .selectFrom('telemetryDailyAgg')
    .selectAll()
    .where('assetId', '=', assetId)
    .where('day', '>=', windowStart)
    .where('day', '<=', windowEnd)
    .execute();

  // 3. Map each day's row → engine inputs, then roll the window up into one
  // input set (max-of-maxes / sum / latest per telemetryName - see
  // telemetry-window-rollup.ts).
  const dailyInputs: DailyTelemetryInputs[] = windowRows.map((row) => ({
    day: toDate(row.day),
    inputs: mapDailyAggToTelemetryInputs(row, MODEL_TELEMETRY_NAMES),
  }));
  const rolledUpInputs = rollupTelemetryInputsOverWindow(dailyInputs);

  // 3b. SIL-150 - per-field source precedence (datadog > csv > model-derived).
  // telemetry_daily_agg mixes sources with no distinction (see module
  // header), so we resolve precedence from telemetry_raw's row-level
  // collection_method_id over a short freshness window and let the result
  // OVERRIDE the corresponding window-rolled values.
  const freshnessWindowStart = new Date(
    windowEnd.getTime() - FIELD_SOURCE_FRESHNESS_WINDOW_DAYS * 24 * 60 * 60 * 1000,
  );
  const windowEndExclusive = new Date(windowEnd.getTime() + 24 * 60 * 60 * 1000); // windowEnd is a day; include its full 24h
  const nodeIds = [...new Set(windowRows.map((row) => row.nodeId))];
  const rawFreshnessRows =
    nodeIds.length > 0
      ? await db
          .selectFrom('telemetryRaw')
          .select([
            'ts',
            'nodeId',
            'collectionMethodId',
            'extraMetrics',
            'gpuTempC',
            'cpuTempC',
            'inletAirTempC',
            'powerDrawWatts',
            'eccErrorCount',
            'xidErrorCode',
            'throttleEventCount',
            'eventLogCriticalCount',
            'gpuMemoryUsedMb',
          ])
          .where('nodeId', 'in', nodeIds)
          .where('ts', '>=', freshnessWindowStart)
          .where('ts', '<', windowEndExclusive)
          .execute()
      : [];

  const rawObservations = rawFreshnessRows.flatMap((row) =>
    mapRawRowToFieldObservations(row, MODEL_TELEMETRY_NAMES),
  );
  const precedence = resolveFieldSourcePrecedence(
    rawObservations,
    windowEndExclusive,
    FIELD_SOURCE_FRESHNESS_WINDOW_DAYS,
  );
  const precedenceResolvedInputs = mergePrecedenceOverBase(rolledUpInputs, precedence);

  // 3c. SIL-150 - trend-derived fields (provisional pending SIL-145; see
  // telemetry-trend.ts module header), only filling names precedence
  // resolution + the window rollup didn't already cover.
  const presentNames = new Set(precedenceResolvedInputs.map((i) => i.Telemetry_Name));
  const trendFallbacks: TelemetryInputLike[] = [];

  if (!presentNames.has('ECC_Correctable_Growth_Rate')) {
    const rate = computeGrowthRate(
      windowRows.map((row) => ({ day: toDate(row.day), value: Number(row.totalEccErrors ?? 0) })),
    );
    if (rate !== null) trendFallbacks.push({ Telemetry_Name: 'ECC_Correctable_Growth_Rate', Value_Num: rate });
  }
  if (!presentNames.has('ECC_Uncorrectable_Growth_Rate')) {
    const series = windowRows
      .map((row) => ({
        day: toDate(row.day),
        value: extraAggregateNumber(row.extraAggregates, 'ECC_Uncorrectable_Aggregate'),
      }))
      .filter((p): p is { day: Date; value: number } => p.value !== null);
    const rate = computeGrowthRate(series);
    if (rate !== null) trendFallbacks.push({ Telemetry_Name: 'ECC_Uncorrectable_Growth_Rate', Value_Num: rate });
  }
  if (!presentNames.has('GPU_Power_Draw_Variance_Percent')) {
    const variance = computeVariancePercent(
      windowRows.map((row) => row.avgPowerDrawWatts).filter((v): v is number => v !== null),
    );
    if (variance !== null) {
      trendFallbacks.push({ Telemetry_Name: 'GPU_Power_Draw_Variance_Percent', Value_Num: variance });
    }
  }
  if (!presentNames.has('GPU_Clock_Variance_Percent')) {
    const variance = computeVariancePercent(
      windowRows.map((row) => row.avgGpuCoreClockMhz).filter((v): v is number => v !== null),
    );
    if (variance !== null) {
      trendFallbacks.push({ Telemetry_Name: 'GPU_Clock_Variance_Percent', Value_Num: variance });
    }
  }

  const inputs = [...precedenceResolvedInputs, ...trendFallbacks];

  // 4-6. Score → coverage.
  const { categories, features } = scoreAsset(telemetryScoringModel, inputs);
  const coverage = computeCoverageFromInputs(telemetryScoringModel.rules, inputs);
  const categoriesWithCoverage = mergeCategoryScoresWithCoverage(categories, coverage);

  const overallScore = Math.round(
    categories.reduce((sum, cat) => sum + cat.score * cat.totalFieldWeight, 0) /
      Math.max(
        1,
        categories.reduce((sum, cat) => sum + cat.totalFieldWeight, 0),
      ),
  );

  // RUF-552 (passport engine, D2/D4/D5) - derived from this same run.
  const horizonBand = horizonBandFromScore(overallScore);

  const inputByName = new Map(inputs.map((i) => [i.Telemetry_Name, i.Value_Num]));
  const trajectory = wearTrajectory({
    eccCorrectableGrowthRate: inputByName.get('ECC_Correctable_Growth_Rate'),
    eccUncorrectableGrowthRate: inputByName.get('ECC_Uncorrectable_Growth_Rate'),
    rowRemapCountInWindow: inputByName.get('Row_Remap_Count'),
    windowDays,
  });

  // Disclosures (D2) + duration tier (D5) both read the asset's FULL lifetime
  // telemetry_daily_agg history, deliberately NOT the scoring window above -
  // an incident months outside every scoring window must still flag the
  // asset, and duration must reflect the whole observed span, not just this
  // run's window.
  const lifetimeRows = await db
    .selectFrom('telemetryDailyAgg')
    .selectAll()
    .where('assetId', '=', assetId)
    .orderBy('day', 'asc')
    .execute();

  const lifetimeDailyInputs: DailyTelemetryInputs[] = lifetimeRows.map((row) => ({
    day: toDate(row.day),
    inputs: mapDailyAggToTelemetryInputs(row, MODEL_TELEMETRY_NAMES),
  }));

  const disclosures = computeTelemetryDisclosures(aggregateLifetimeDisclosureInputs(lifetimeDailyInputs));

  const firstDay = toDate(lifetimeRows[0]!.day);
  const lastDay = toDate(lifetimeRows[lifetimeRows.length - 1]!.day);
  const duration = durationTierFromDates(firstDay, lastDay);
  const baseConfidence = confidenceTier(coverageTier(coverage.overallCoverage), duration);

  // SIL-150 - measured (live Datadog) field weight, as a fraction of the
  // model's total field weight, boosts confidence one tier when a
  // meaningful share of the score came from a live source within the
  // freshness window (see confidence-tier.ts's applyMeasuredBoost).
  const coverageDenominators = buildCoverageDenominators(telemetryScoringModel.rules);
  const measuredWeight = telemetryScoringModel.rules
    .filter((rule) => precedence.measuredNames.has(rule.telemetryName))
    .reduce((sum, rule) => sum + rule.fieldWeight, 0);
  const measuredWeightFraction =
    coverageDenominators.overallTotal > 0 ? measuredWeight / coverageDenominators.overallTotal : 0;

  // SIL-581/§9 - Active Verification confidence bump. Read the asset's
  // verification_run rows within the TTL window (729's partial index covers
  // the qualifying-candidate half of this; the plain window scan below also
  // catches failed runs, which block the bump - worst-run-in-window governs).
  // Composed with the measured-boost fraction above under a combined
  // ONE-STEP-TOTAL cap (applyCappedConfidenceBumps) - see
  // verification-confidence.ts's module header for why the two bumps must
  // never stack.
  const now = opts.scoredAt ?? new Date();
  const verificationTtlCutoff = new Date(now.getTime() - VERIFICATION_BUMP_TTL_DAYS * 24 * 60 * 60 * 1000);
  const verificationRunRows = await db
    .selectFrom('verificationRun')
    .select(['tier', 'status', 'verdict', 'completedAt', 'rooflineResult', 'attestedSerial'])
    .where('assetId', '=', assetId)
    .where('status', '=', 'results_ready')
    .where('completedAt', 'is not', null)
    .where('completedAt', '>=', verificationTtlCutoff)
    .where('completedAt', '<=', now)
    .execute();

  // SIL-586/§7 — the Tier 1 serial-match gate compares each run's attested
  // serial to the OWNING asset's declared serial_number. One lookup here
  // (not per-row) since `verificationRunRows` is already scoped to this
  // single asset.
  const assetForSerial = await db
    .selectFrom('asset')
    .select(['serialNumber'])
    .where('id', '=', assetId)
    .executeTakeFirst();
  const assetSerialNumber = assetForSerial?.serialNumber ?? null;

  const verificationRunsForConfidence: VerificationRunForConfidence[] = verificationRunRows.map((row) => ({
    tier: row.tier as 1 | 2 | 3,
    status: row.status,
    verdict: row.verdict,
    completedAt: row.completedAt ? toDate(row.completedAt) : null,
    rooflinePlausible:
      row.rooflineResult != null &&
      typeof row.rooflineResult === 'object' &&
      !Array.isArray(row.rooflineResult) &&
      (row.rooflineResult as Record<string, unknown>).plausible === true,
    attestedSerial: row.attestedSerial,
    assetSerialNumber,
  }));
  const verificationEffect = resolveVerificationConfidenceEffect(verificationRunsForConfidence, now);

  const confidence = applyCappedConfidenceBumps(baseConfidence, {
    measuredWeightFraction,
    verificationQualifies: verificationEffect.qualifiesForBump,
  });

  // 7. Persist snapshot. source_day is kept = windowEnd (the latest day
  // scored) for backward compatibility with existing "latest day" consumers
  // (assets.ts API route, operations asset detail page, this module's own
  // telemetry-ingest eligibility query); window_start/window_end are the
  // RUF-517 authoritative stamp of what was actually rolled up.
  const inserted = await db
    .insertInto('telemetryScoreSnapshot')
    .values({
      assetId,
      sourceDay: latestAgg.day,
      windowStart,
      windowEnd,
      modelVersion: telemetryScoringModel.modelVersion,
      triggerId,
      overallScore,
      overallCoverage: String(coverage.overallCoverage),
      // Explicit JSON.stringify - jsonb ARRAY columns, not objects; pg's
      // parameter binding would otherwise render a JS array as a Postgres
      // ARRAY literal instead of JSON.
      categories: JSON.stringify(categoriesWithCoverage) as never,
      features: JSON.stringify(features) as never,
      disclosures: JSON.stringify(disclosures) as never,
      horizonBand,
      wearTrajectory: trajectory,
      confidenceTier: confidence,
      ...(opts.scoredAt ? { scoredAt: opts.scoredAt } : {}),
    })
    .returningAll()
    .executeTakeFirstOrThrow();

  return { scored: true, snapshot: inserted };
}
